import type {
  Dosage,
  DosageMatrixValue,
  TimeOfDay
} from './medicationDosageMatrixTypes';

export const INPUT_MAX_LEN = 5;

export const defaultMatrixValue: DosageMatrixValue = {
  morning: null,
  noon: null,
  evening: null,
  night: null
};

export const extractMatrixString = (
  dosage: string | undefined
): string | null => {
  if (!dosage) {
    return null;
  }
  return getDeserializationRegex().exec(dosage)?.[2] ?? null;
};

export const removeMatrixValue = (
  dosage: string | undefined,
  matrixValue?: DosageMatrixValue | null
) => {
  if (!dosage) {
    return dosage;
  }
  const ser =
    matrixValue !== undefined
      ? serialize(matrixValue)
      : extractMatrixString(dosage);
  if (!ser) {
    return dosage;
  }
  let d = dosage.replace(ser, '').trim();
  if (d.endsWith('/')) {
    d = d.slice(0, -1).trim();
  }

  return d;
};

export const deserialize = (
  val: string | undefined
): DosageMatrixValue | null => {
  if (!val) {
    return null;
  }
  const serialized = getDeserializationRegex().exec(val)?.[2];
  if (!serialized) {
    return null;
  }

  const parts = serialized.split('-');

  return {
    morning: toDosageValue(parts[0]),
    noon: toDosageValue(parts[1]),
    evening: toDosageValue(parts[2]),
    night: toDosageValue(parts[3])
  };
};

export const serialize = (
  val: DosageMatrixValue | undefined | null
): string => {
  if (!val) {
    return '';
  }
  const str = Object.entries(val)
    .sort(sortByToD)
    .map(([, dosage]) => {
      if (dosage === 0.5) {
        return '½';
      }

      return dosage || 0;
    })
    .join('-');

  return str === '0-0-0-0' ? '' : str;
};

function toDosageValue(s: string): Dosage {
  const str = s?.trim();
  if (!str || str === '0') {
    return null;
  }

  if (str === '½') {
    return 0.5;
  }

  if (str === '0.5' || str === '1' || str === '2') {
    return +str as Extract<Dosage, number>;
  }

  return str.trim();
}

function sortByToD(entryA: [string, Dosage], entryB: [string, Dosage]) {
  const [a] = entryA as [TimeOfDay, Dosage];
  const [b] = entryB as [TimeOfDay, Dosage];
  if (a === b) {
    return 0;
  }

  if (a === 'morning') {
    return -1;
  } else if (b === 'morning') {
    return 1;
  }

  if (a === 'night') {
    return 1;
  } else if (b === 'night') {
    return -1;
  }

  if (a === 'noon') {
    return -1;
  }

  if (b === 'noon') {
    return 1;
  }

  return 0;
}

let deserializationRegex: RegExp | undefined;

function getDeserializationRegex() {
  if (!deserializationRegex) {
    deserializationRegex = new RegExp(
      `(^|\\/)((([½\\w\\s]{0,${INPUT_MAX_LEN}})-){3}[½\\w\\s]{0,${INPUT_MAX_LEN}})`
    );
  }

  return deserializationRegex;
}
